import { OmsInputsMessengerTemplatedMessageType, TemplateEntryPointEnum } from '__generated__/types';
import { TEventBusEventCallback } from '@xometry/event-bus';
import {
  EMessengerChatSourceType,
  EMessengerEventBusEvents,
  IEventBusMessengerEvents,
  MessengerEventBus,
} from '@xometry/ui';
import { FormInstance, Modal } from 'antd';
import { useAntNotification } from 'components/ant/AntNotification';
import { AppContext } from 'contexts/AppContext';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useStore } from 'stores/RootStore';
import { ESideOverlayType } from 'stores/SideOverlay/SideOverlayStore';
import { TemplateFragment } from 'utils/graphql/fragments/__generated__/template';
import { useMessageCreateFromTemplateMutation } from 'utils/graphql/mutations/__generated__/messageCreateFromTemplate';
import { useTemplateModalQuery } from 'utils/graphql/queries/__generated__/templateModal';
import { DeviceTypes, useDeviceType } from 'utils/hooks/useDeviceType';
import { useEmbedded } from 'utils/hooks/useEmbedded';
import { useMessengerEventBus } from 'utils/hooks/useMessengerEventBus';

import { TemplateModalFormFieldEnum, TemplateModalFormValues } from './TemplateModal.types';
import { useBulkSendOfferMessagesFromTemplate } from './useBulkSendOfferMessagesFromTemplate/useBulkSendOfferMessagesFromTemplate';

interface UseTemplatesModalArgs {
  form: FormInstance<TemplateModalFormValues>;
  selectedTemplate: Omit<TemplateFragment, 'content'> | null;
  selectedCategoryId: string | null;
  selectedTemplateGroupId: string | null;
}

const IGNORED_FIELDS = [
  TemplateModalFormFieldEnum.Category,
  TemplateModalFormFieldEnum.TemplateConstantName,
  TemplateModalFormFieldEnum.Locale,
];

export const useTemplatesModal = ({ form, selectedTemplate, selectedTemplateGroupId }: UseTemplatesModalArgs) => {
  const notification = useAntNotification();
  const changesMadeRef = useRef(false);
  const { messengerEventBus } = useContext(AppContext);
  const { sendEvent } = useMessengerEventBus();
  const deviceType = useDeviceType();
  const { sideOverlayStore, templatesModalStore } = useStore();
  const { isGshEmbedded } = useEmbedded();

  const { sendBulkOfferMessages, loading: bulkSendLoading } = useBulkSendOfferMessagesFromTemplate();

  const isMobile = deviceType === DeviceTypes.MOBILE && !isGshEmbedded;

  const { data: templateModalData } = useTemplateModalQuery({
    variables: {
      ...(templatesModalStore.categoriesRequestParams as NonNullable<
        typeof templatesModalStore.categoriesRequestParams
      >),
    },
    fetchPolicy: 'no-cache',
    skip: !templatesModalStore.isOpen || !templatesModalStore.categoriesRequestParams,
  });

  const [createMessageFromTemplate, { loading: messageCreateLoading }] = useMessageCreateFromTemplateMutation();

  /** EventBus event handler */
  const handleOpenTemplatesModal: TEventBusEventCallback<
    IEventBusMessengerEvents,
    EMessengerEventBusEvents.OPEN_TEMPLATES_MODAL
  > = useCallback(
    ({ chatType, sourceId, sourceType, isVisibleToClient, isVisibleToPartner }) => {
      let entryPoint: TemplateEntryPointEnum | null = null;

      if (chatType === 'document') {
        entryPoint = TemplateEntryPointEnum.DocumentMessenger;
      } else if (chatType === 'general') {
        if (sourceType === EMessengerChatSourceType.CUSTOMER) {
          entryPoint = TemplateEntryPointEnum.GeneralMessengerPerson;
        } else if (sourceType === EMessengerChatSourceType.PARTNER) {
          entryPoint = TemplateEntryPointEnum.GeneralMessengerProvider;
        }
      }

      if (!entryPoint) {
        console.error('Unable to open templates modal. `entryPoint` is null.');

        return;
      }

      templatesModalStore.openTemplatesModal({
        entryPoint,
        messageSourceForTemplates: { sourceId, sourceType },
        isVisibleToClient,
        isVisibleToPartner,
      });
    },
    [templatesModalStore],
  );

  const handleValuesChange = useCallback((values: Partial<TemplateModalFormValues>) => {
    if (!changesMadeRef.current) {
      const hasRelevantChanges = Object.keys(values).some(
        (key) => !IGNORED_FIELDS.includes(key as TemplateModalFormFieldEnum),
      );

      if (hasRelevantChanges) {
        changesMadeRef.current = true;
      }
    }
  }, []);

  const handleCloseTemplatesModal = () => {
    if (changesMadeRef.current) {
      Modal.confirm({
        title: 'Close Message Center Templates?',
        content: 'Changes you made may not be saved',
        onOk() {
          changesMadeRef.current = false;
          templatesModalStore.closeTemplatesModal();
          form.resetFields();
        },
        onCancel() {
          return;
        },
      });
    } else {
      templatesModalStore.closeTemplatesModal();
      form.resetFields();
    }
  };

  const handleSendMessage = () => {
    const formValues = form.getFieldsValue();
    const { messageSourceForMessage: messageSource, messageVisibilityAttributes } = templatesModalStore;

    if (!selectedTemplate || !messageSource) {
      return;
    }

    const attachmentUuids = formValues?.attachments
      ?.filter((attachment) => !attachment.value)
      .map((attachment) => attachment.uuidId);

    const prepareAttachments = formValues?.attachments
      ?.filter((attachment) => attachment.value)
      .map((attachment) => attachment.value) as string[];

    const attributes: Required<OmsInputsMessengerTemplatedMessageType> = {
      content: formValues.content,
      mentions: formValues.mentions,
      sourceId: messageSource.sourceId,
      sourceType: messageSource.sourceType,
      isVisibleToClient: messageVisibilityAttributes.isVisibleToClient,
      isVisibleToPartner: messageVisibilityAttributes.isVisibleToPartner,
      to: formValues.to,
      cc: formValues.cc,
      bcc: formValues.bcc,
      attachmentUuids,
      prepareAttachments,
    };

    void createMessageFromTemplate({ variables: { input: { attributes, templateId: selectedTemplate.id } } }).then(
      () => {
        if (sideOverlayStore.context.type == ESideOverlayType.Messenger) {
          sendEvent?.(EMessengerEventBusEvents.REFETCH_MESSAGES, {
            sourceId: messageSource.sourceId,
            sourceType: messageSource.sourceType,
          });
        }

        changesMadeRef.current = false;
        notification.success({ message: 'Message has been successfully sent from template.' });
        handleCloseTemplatesModal();
      },
    );
  };

  const handleBulkSendMessages = () => {
    const { bulkSourceId: rfqId, bulkSourceItemIds: rfqOfferIds } = templatesModalStore;

    if (!rfqId || !rfqOfferIds || !selectedTemplateGroupId) return;

    void sendBulkOfferMessages({
      rfqId,
      rfqOfferIds,
      templateGroupId: selectedTemplateGroupId,
    }).then(() => {
      handleCloseTemplatesModal();
    });
  };

  const addEventListeners = useCallback(
    (eb: MessengerEventBus) => {
      eb.addEventListener(EMessengerEventBusEvents.OPEN_TEMPLATES_MODAL, handleOpenTemplatesModal);
    },
    [handleOpenTemplatesModal],
  );

  const removeEventListeners = useCallback(
    (eb: MessengerEventBus) => {
      eb.removeEventListener(EMessengerEventBusEvents.OPEN_TEMPLATES_MODAL, handleOpenTemplatesModal);
    },
    [handleOpenTemplatesModal],
  );

  useEffect(() => {
    if (messengerEventBus) {
      addEventListeners(messengerEventBus);

      return () => {
        removeEventListeners(messengerEventBus);
      };
    }
  }, [addEventListeners, messengerEventBus, removeEventListeners]);

  const messageCreateLoadingResolved = templatesModalStore.isBulkActions ? bulkSendLoading : messageCreateLoading;

  return {
    changesMadeRef,
    handleCloseTemplatesModal,
    handleSendMessage: templatesModalStore.isBulkActions ? handleBulkSendMessages : handleSendMessage,
    handleValuesChange,
    messageCreateLoading: messageCreateLoadingResolved,
    templateModalData,
    templatesModalVisibility: templatesModalStore.isOpen,
    isMobile,
    isGshEmbedded,
    isVisibleToClient: templatesModalStore.messageVisibilityAttributes.isVisibleToClient,
    isVisibleToPartner: templatesModalStore.messageVisibilityAttributes.isVisibleToPartner,
  };
};
